import styled from 'styled-components';

import { ANIM, LOGIN, LOGIN_MOBILE, REGISTER } from '../../theme/tokens';

/**
 * Register page styles: a hand-written, token-only reproduction of the
 * config-driven `module.owners.hub.register` stylesheet. It is the auth sibling
 * of `/login` and shares its whole design, so the shared surfaces (background,
 * body card, input, CTA, warning, footer) reuse the `LOGIN` token group and the
 * title sweep reuses `LOGIN.titleGradient`. The card is KEPT-DARK: every LOGIN
 * colour resolves to its dark literal in both themes (see KEPT_DARK_LOGIN in
 * theme/themePalette.ts), so nothing in here is theme-scoped; only the
 * marketing-consent checkbox brings its own colours (`REGISTER`). The node
 * tree keeps the original class names (`.page`, `.main`, `.body`, `.sign-in-form`,
 * …) so the markup in `index.tsx` stays a 1:1 mirror of the template. The
 * `@media (max-width: 768px)` block is the source ModuleElement's own responsive
 * reflow.
 */
export const Register = styled.div`
  width: 100dvw;
  min-height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: ${LOGIN.pageBg};
  background-image: ${LOGIN.bgImage};
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: cover;

  /* Firefox */
  & {
    scrollbar-color: ${LOGIN.scrollColorThumb} ${LOGIN.scrollColorTrack};
  }

  /* Chrome, Edge, and Safari */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${LOGIN.scrollTrack};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${LOGIN.scrollThumb};
    border-radius: 10px;
    border: 3px solid ${LOGIN.scrollThumbBorder};
  }

  /* The app sets \`color-scheme: dark\` globally, which flips the UA's default
     form-control text colour to white and turns the white email field
     white-on-white. Force the light UA scheme on text inputs so their text is
     dark again. */
  & input:not([type='checkbox']):not([type='radio']) {
    color-scheme: light;
  }

  /* Vertically centred (per the Figma frame): the logo + card block sits in the
     middle of the viewport. min-height + center keeps it centred on tall screens;
     the vertical padding on .main plus the root's overflow-y:auto lets a viewport
     shorter than the content scroll instead of clipping. The login<->register
     morph re-centres the block as the card height changes. */
  .page {
    width: 100%;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .main {
    width: 480px;
    padding: 48px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    /* 64px between the three groups (logo / card / language selector) per Figma. */
    gap: 64px;
  }

  .header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .logo {
    width: 200px;
    display: flex;

    svg,
    img {
      width: 100%;
      height: auto;
      cursor: pointer;
    }
  }

  /* The card hugs its content: its height is driven by the inner wrapper's
     natural height (set inline by useAuthCardMorph) and eased, so it morphs
     smoothly between the sign-in and sign-up heights and the email-sent view.
     The padding lives on \`.body-inner\` so the measured height already includes
     it. \`flex-shrink: 0\` keeps it from compressing on a short viewport. */
  .body {
    width: 480px;
    flex-shrink: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    /* Top-pinned, NOT centred: at rest the inline morph height (useAuthCardMorph) equals the
       content height so this is invisible, but DURING the height transition it keeps the content
       anchored to the top so only the card's bottom edge grows/shrinks. Centring here made the
       inner content slide vertically for the ~350ms of the morph (the "dance" on mode toggle and
       the login<->register swap). */
    justify-content: flex-start;
    overflow: hidden;
    /* The card's own padding lives on \`.body-inner\` (so the measured morph
       height already includes it); the card itself carries none. */
    padding: 0;
    border-radius: 24px;
    background: ${LOGIN.bodyBg};
    box-shadow: ${LOGIN.cardShadow};
    transition: height ${ANIM.DRAWER_MS}ms ${ANIM.EASE};
  }

  .body-inner {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    padding: 24px;
  }

  @media (prefers-reduced-motion: reduce) {
    .body {
      transition: none;
    }
    /* Strength meter: reveal + bar-fill motion snaps; only the colour still transitions. */
    .input .password-strength {
      transition: none;
      transform: none;

      .strength-bars .bar::before {
        transition: background-color ${ANIM.INTERACT_MS}ms ease;
      }
    }
  }

  .layout {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 64px;
  }

  /* Sign-up (form) view only — the email-sent confirmation keeps the centred base.
     The card is a left-aligned, content-height stack on a uniform 24px rhythm:
     heading / input (+ consent) / button (inside the form) then the divider and
     footer (siblings of the form). The heading's title->description gap is 8. */
  .wrapper.form-view {
    height: auto;
    justify-content: flex-start;
    align-items: stretch;
    gap: 24px;

    /* The form's gap is the tight input->CTA pairing (12), NOT the card's 24 rhythm.
       The heading is the form's first child, so it makes up the difference itself and
       still sits 24 from the field. */
    .sign-in-form {
      height: auto;
      align-items: stretch;
      gap: 12px;
    }

    .heading {
      align-items: flex-start;
      margin-bottom: 12px;
    }

    .title {
      max-width: none;
      text-align: left;
    }

    .subtitle {
      text-align: left;
    }

    .cta {
      align-items: stretch;
    }

    .note {
      text-align: left;
    }

    .footer {
      justify-content: flex-start;
    }
  }

  /* Email-sent (magic-link) view — the same left-aligned glass card on a 24px
     rhythm: a round back button + heading, the email chip, the paper-plane art,
     a styled divider, then the expiry note. */
  .wrapper.sent-view {
    height: auto;
    justify-content: flex-start;
    align-items: stretch;
    gap: 24px;

    .heading {
      align-items: flex-start;
      gap: 12px;
    }

    .heading-copy {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      width: 100%;
    }

    .title {
      max-width: none;
      text-align: left;
    }

    .subtitle {
      text-align: left;
    }

    /* Round back button (returns to the sign-up form). */
    .back {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      border: 0;
      border-radius: 9999px;
      background: ${LOGIN.cardChipBg};
      color: ${LOGIN.cardChipText};
      cursor: pointer;
      transition: filter ${ANIM.INTERACT_MS}ms ease;

      .bi {
        font-size: 16px;
        display: inline-flex;
      }

      &:hover {
        filter: brightness(1.3);
      }
    }

    /* Pill showing the address the link was sent to (envelope + email). */
    .email-chip {
      align-self: flex-start;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      max-width: 100%;
      height: 40px;
      padding: 8px 16px;
      border-radius: 9999px;
      background: ${LOGIN.cardChipBg};
      color: ${LOGIN.cardChipText};

      .icon {
        font-size: 16px;
        display: inline-flex;
        flex-shrink: 0;
      }
      .email {
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    /* The source illustration (a 381x282 SVG) carries a lot of empty margin: the
       artwork's real bounding box is only x 58-356, y 108-218. So the band still
       crops that margin away, but its height TRACKS its width instead of being a
       fixed 148px: cover's scale is width-driven here (the image always meets the
       band edge to edge horizontally), so a fixed height meant a wider card scaled
       the artwork UP against a frozen band and clipped the plane and its trail.
       With the ratio pinned, the whole illustration scales with the container and
       stays contained at every width. 432x148 is the desktop geometry (480px card
       minus 2x24 padding, and the design's band height); max-width holds the art at
       that design size on the wider fluid card below the breakpoint. */
    .art {
      width: 100%;
      max-width: 432px;
    }

    /* The ratio lives on the image, not on \`.art\`: \`.art\` is a column-flex item,
       so its automatic minimum height would be floored by the image's own intrinsic
       height and the ratio would not hold. */
    .mail-sent {
      display: block;
      width: 100%;
      height: auto;
      aspect-ratio: 432 / 148;
      object-fit: cover;
      /* The artwork's vertical mid-point sits at 58% of the source height; 64%
         parks that mid-point in the middle of the cropped band. */
      object-position: center 64%;
    }

    .warning {
      position: static;
      text-align: left;
      color: ${LOGIN.warning};
      font-size: 12px;
      font-weight: 500;
      line-height: 1.1;
    }
  }

  .sign-in-form {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    gap: 48px;
  }

  .heading {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }

  .title {
    max-width: 420px;
    font-size: 36px;
    font-style: normal;
    font-weight: 800;
    line-height: 1.12;
    text-align: center;
    background: ${LOGIN.titleGradient};
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    /* Same descender clipping as /login (see the note on \`.title\` there): the gradient
       is only painted inside this box, which the 1.12 line-height makes shorter than
       Satoshi 800's own font box, so the \`g\` of "iGaming" lost its tail. Padding
       extends the paint area, the negative margin keeps the layout unchanged. On the
       wrapped two-line title only the LAST line clips, the interior ones sit inside
       the box already. */
    padding-bottom: 4px;
    margin-bottom: -4px;
  }

  .subtitle {
    color: ${LOGIN.subtitle};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.43;
    text-align: center;
  }

  .address {
    color: ${LOGIN.address};
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
  }

  /* Sign-up method switch: a compact two-pill segmented control on the card's own rhythm.
     Understated per the "start subtle" working agreement, a grey track with a slightly lighter
     active pill, no brand colour yet. Copied from /login's .mode-toggle so the two auth screens
     read as one control. */
  .mode-toggle {
    width: 100%;
    display: flex;
    gap: 4px;
    padding: 4px;
    border-radius: 9999px;
    background: ${LOGIN.tipBg};

    .mode-option {
      flex: 1;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: 9999px;
      background: transparent;
      color: ${LOGIN.subtitle};
      font-size: 13px;
      font-style: normal;
      font-weight: 500;
      line-height: 1;
      cursor: pointer;
      transition:
        background-color ${ANIM.INTERACT_MS}ms ease,
        color ${ANIM.INTERACT_MS}ms ease;

      &.active {
        background: ${LOGIN.cardChipBg};
        color: ${LOGIN.cardChipText};
      }
    }
  }

  /* Register failure notice — a red-tinted glass panel between the heading and the input, shown
     when a password register could not complete (the /auth/register-password endpoint is 404
     until BE ships). Same treatment as the /login expired-link notice; the ResizeObserver in
     useAuthCardMorph re-measures the card as it appears. */
  .link-notice {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid ${LOGIN.errorBoxBorder};
    background: ${LOGIN.errorBoxBg};
    text-align: left;

    .notice-icon {
      flex-shrink: 0;
      font-size: 16px;
      line-height: 20px;
      color: ${LOGIN.errorBoxIcon};
      display: inline-flex;
    }

    .notice-copy {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .notice-title {
      color: ${LOGIN.errorBoxText};
      font-size: 14px;
      font-weight: 600;
      line-height: 1.35;
    }

    .notice-hint {
      color: ${LOGIN.subtitle};
      font-size: 12px;
      font-weight: 400;
      line-height: 1.4;
    }
  }

  .input {
    width: 100%;
    display: flex;
    flex-direction: column;
    /* Holds the email field AND the consent checkbox, so this gap is the FIRST of the
       three 12px steps down the form (field -> consent -> CTA). The consent -> CTA step
       comes from the form's own gap, in the form-view block above. */
    gap: 12px;

    input[type='email'] {
      height: 48px;
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 0 12px;
      border-radius: 999px;
      border: 1px solid ${LOGIN.inputBorder};
      background: ${LOGIN.inputBg};
      outline: 0;

      /* Focus recolours that SAME 1px edge in place, in BOTH themes: the card is
         kept-dark, so the field is one white pill on one near-black card everywhere and
         a theme-scoped focus rule would only re-open the per-theme split this card
         exists to close. Deliberately only the colour: no second ring outside it (an
         outline or a spread shadow would read as a double edge on a pill this round) and
         no width change (that would reflow the text inside a border-box control). The
         edge is the boundary between the white fill and the near-black card, so its
         colour has to clear 3:1 against BOTH; \`LOGIN.inputFocusBorder\` documents why
         zinc-500 is the one that does. */
      transition: border-color ${ANIM.INTERACT_MS}ms ease;

      &:focus-visible {
        border-color: ${LOGIN.inputFocusBorder};
      }

      &::placeholder {
        color: ${LOGIN.inputPlaceholder};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
      }
    }

    /* Optional password row. Register scopes the pill metrics above to \`input[type='email']\`
       only, so the password field (password when hidden, text when the eye reveals it) needs
       its own full metrics on BOTH types, or the revealed field falls back to the UA's default
       and visibly shrinks (the eye-toggle-shrink lesson from /login). The relative wrapper lets
       the show/hide button sit INSIDE the pill; \`padding-right\` clears it so typed text never
       runs under the glyph. */
    .password-input {
      position: relative;

      input[type='password'],
      input[type='text'] {
        width: 100%;
        height: 48px;
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 0 44px 0 12px;
        border-radius: 999px;
        border: 1px solid ${LOGIN.inputBorder};
        background: ${LOGIN.inputBg};
        outline: 0;
        transition: border-color ${ANIM.INTERACT_MS}ms ease;

        &:focus-visible {
          border-color: ${LOGIN.inputFocusBorder};
        }

        &::placeholder {
          color: ${LOGIN.inputPlaceholder};
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 20px;
        }
      }

      /* Borderless icon button, absolutely centred on the field's right edge: an understated
         text-coloured glyph with a subtle hover lift, so it reads as part of the field rather
         than a second control (matches /login). */
      .eye-toggle {
        position: absolute;
        top: 50%;
        right: 4px;
        transform: translateY(-50%);
        width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: 0;
        background: transparent;
        color: ${LOGIN.subtitle};
        cursor: pointer;
        transition: color ${ANIM.INTERACT_MS}ms ease;

        .bi {
          font-size: 18px;
          display: inline-flex;
        }

        &:hover {
          color: ${LOGIN.inputPlaceholder};
        }
      }
    }

    /* Password strength meter + caption. The block keeps its reserved height at all times, but
       stays hidden (opacity/visibility, NOT display, so the reserved box holds and the fade can
       transition) until the first keystroke, then fades in. The bars fill left-to-right as a
       positional gradient: bar 1 orange (red only in the too-short state, <8 = cannot submit),
       bar 2 amber, bar 3 green, so the meter reads too-short(red) -> weak(orange) -> fair -> strong.
       The caption colour tracks the current state. */
    .password-strength {
      display: flex;
      flex-direction: column;
      gap: 6px;
      /* Reserve the meter + one caption line up front so the block never changes height. */
      min-height: 30px;
      text-align: left;

      /* Hidden until the user types (reveal is anchored to the keystroke, not to mount). The
         reserved height above holds, so the CTA never shifts; only opacity/transform animate. */
      opacity: 0;
      visibility: hidden;
      transform: translateY(-4px);
      transition:
        opacity ${ANIM.DRAWER_MS}ms ${ANIM.EASE},
        transform ${ANIM.DRAWER_MS}ms ${ANIM.EASE},
        visibility 0s linear ${ANIM.DRAWER_MS}ms;

      &.is-visible {
        opacity: 1;
        visibility: visible;
        transform: none;
        /* Show immediately (no visibility delay), else the meter appears a beat late on the
           first keystroke. */
        transition:
          opacity ${ANIM.DRAWER_MS}ms ${ANIM.EASE},
          transform ${ANIM.DRAWER_MS}ms ${ANIM.EASE},
          visibility 0s;
      }

      .strength-bars {
        display: flex;
        gap: 6px;

        .bar {
          flex: 1;
          height: 4px;
          position: relative;
          overflow: hidden;
          border-radius: 999px;
          background: ${LOGIN.strengthTrack};

          /* The fill grows in from the left when the bar becomes filled (score crosses its
             threshold); an already-filled bar stays at scaleX(1) and only its colour transitions
             on a level change, so typing within a level does not re-animate. */
          &::before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: inherit;
            transform: scaleX(0);
            transform-origin: left;
            transition:
              transform ${ANIM.INTERACT_MS}ms ease,
              background-color ${ANIM.INTERACT_MS}ms ease;
          }

          &.filled::before {
            transform: scaleX(1);
          }
        }
      }

      .strength-caption {
        font-size: 12px;
        font-weight: 500;
        line-height: 1.4;
        /* One line, reserved even when the caption is empty (password blank). */
        min-height: 17px;
        color: ${LOGIN.subtitle};
      }

      /* Uniform colour per state: every filled bar AND the caption take the current level's
         single colour, so the whole meter recolours together as the state changes (too-short red
         -> weak orange -> fair amber -> strong green). Red stays reserved for the not-submittable
         too-short state (< 8 chars). The number of filled bars still conveys the level; the shared
         colour is what changes. */
      &.strength-too-short {
        .bar.filled::before {
          background: ${LOGIN.strengthTooShort};
        }
        .strength-caption {
          color: ${LOGIN.strengthTooShort};
        }
      }

      &.strength-weak {
        .bar.filled::before {
          background: ${LOGIN.strengthWeak};
        }
        .strength-caption {
          color: ${LOGIN.strengthWeak};
        }
      }

      &.strength-fair {
        .bar.filled::before {
          background: ${LOGIN.strengthFair};
        }
        .strength-caption {
          color: ${LOGIN.strengthFair};
        }
      }

      &.strength-strong {
        .bar.filled::before {
          background: ${LOGIN.strengthStrong};
        }
        .strength-caption {
          color: ${LOGIN.strengthStrong};
        }
      }
    }

    .checkbox {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 8px;
      padding: 0 12px;

      input[type='checkbox'] {
        /* The one control on this card the token system cannot reach. It sets no
           \`appearance: none\`, so the box itself is painted by the user agent, which
           picks its palette from \`color-scheme\` rather than from any of our colours,
           and the rule at the top of this file deliberately excludes checkboxes from
           the \`color-scheme: light\` it forces on text inputs. That was right while
           the card followed the theme; now that the card is kept-dark, the inherited
           scheme was the last thing still flipping on it (a white box on light, a dark
           one on dark, same near-black card). Pin the scheme to match the card. The
           border and radius below are inert for the same reason, kept only because the
           UA honours them once a future restyle adds \`appearance: none\`. */
        color-scheme: dark;
        width: 16px;
        height: 16px;
        border-radius: 7px;
        border: 2px solid ${REGISTER.checkboxBorder};
        margin-top: 2px;
        /* Native checkbox: without this the checked fill is the user agent's stock
           blue, which matches nothing in the palette. The UA still draws the tick and
           picks its contrast against the accent, so only the hue needs naming. */
        accent-color: ${REGISTER.checkboxAccent};
      }

      .label {
        color: ${REGISTER.checkboxLabel};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        opacity: 0.7;

        .text,
        .link {
          display: inline;
          margin-right: 6px;
          cursor: pointer;
        }

        a {
          color: ${REGISTER.checkboxLabel};
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 20px;
          text-decoration-line: underline;
          text-decoration-style: solid;
          text-underline-offset: auto;
        }
      }
    }
  }

  .cta {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .button {
      width: 100%;
      /* Matches the email field's 48px so the two stack as one control pair rather
         than a tall input above a shorter button. */
      height: 48px;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 8px;
      padding: 0 16px;
      border: 0;
      border-radius: 9999px;
      background: ${LOGIN.cta};
      cursor: pointer;
      pointer-events: all;

      .text {
        color: ${LOGIN.ctaText};
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 20px;
      }

      .icon {
        color: ${LOGIN.ctaText};
        font-size: 20px;
        display: inline-flex;
      }

      /* One disabled treatment for both themes, same as /login's CTA (see the note
         there): the card is kept-dark everywhere, so the fade always composites the
         orange fill down against the same near-black surface. The light-only override
         this used to carry existed for a white card that no longer exists. */
      &.disabled,
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
        pointer-events: none;
      }
    }

    .note {
      margin-top: 24px;
      color: ${LOGIN.subtitle};
      font-size: 16px;
      font-style: normal;
      font-weight: 300;
      line-height: 110%;
    }

    .mail-sent {
      width: 75%;
      margin: -20% 0;
    }
  }

  .warning {
    position: absolute;
    bottom: 0;
    color: ${LOGIN.warning};
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 110%;
  }

  /* The hairline between the CTA and the footer. The shared <Divider variant="styled">
     paints itself from GRADIENTS.dividerStyledH, a WHITE lift shared with every other
     surface in the app, so it follows the theme and inverts to a dark line on light,
     which would vanish on this kept-dark card. Repoint it at the card's own token; the
     shared one stays alone so no other divider in the app is dragged dark with it. */
  .auth-divider {
    background: ${LOGIN.cardDivider};
  }

  .footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    .label {
      color: ${LOGIN.footerLabel};
      font-size: 16px;
      font-style: normal;
      font-weight: 300;
      line-height: 110%;
    }

    .link {
      color: ${LOGIN.footerLink};
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 110%;
      text-decoration: none;
    }
  }

  /* The language switcher is its own component (reused from \`pages/login\`); only
     its responsive margin needs the page breakpoint here. */

  ${LOGIN_MOBILE} {
    &,
    .page,
    .main,
    .body,
    .header,
    .footer {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
    }
    & {
      min-height: 100vh !important;
      height: auto !important;
      overflow-y: auto !important;
      justify-content: flex-start !important;
    }
    .main {
      padding: 24px 12px !important;
      /* The desktop 64px inter-group gap is too airy on a phone. */
      gap: 24px !important;
      overflow-x: hidden !important;
    }
    /* Comfortable card padding while the card still nearly spans the screen. */
    .body-inner {
      padding: 24px 20px !important;
    }
    .wrapper,
    .sign-in-form,
    .heading,
    .mode-toggle,
    .input,
    .checkbox,
    .checkbox .label,
    .cta {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    .heading .title {
      font-size: 28px !important;
      line-height: 1.15 !important;
      word-wrap: break-word !important;
    }
    .heading .subtitle {
      font-size: 14px !important;
      line-height: 1.4 !important;
    }
    .button {
      width: 100% !important;
      max-width: 100% !important;
    }
    .header .logo img {
      max-width: 100% !important;
      height: auto !important;
    }
    /* NB: \`.body\` is deliberately NOT in this auto-height reset — its height is the
       inline px value set by useAuthCardMorph, and an \`height: auto !important\` here
       would beat the inline style and kill the morph on mobile. The card stays
       content-driven (the measured height already reflects the mobile padding) and
       keeps its base \`overflow: hidden\` + height transition so it morphs here too. */
    .main,
    .layout,
    .wrapper,
    .sign-in-form,
    .cta {
      height: auto !important;
      min-height: 0 !important;
      max-height: none !important;
      overflow: visible !important;
      justify-content: flex-start !important;
    }
    /* Vertically centre the logo + card + language block (matches desktop); falls
       back to top-anchored + scroll when the content is taller than the viewport. */
    .page {
      min-height: 100dvh !important;
      height: auto !important;
      overflow: visible !important;
      /* Centred, NOT top-anchored: where the content fits (e.g. 844h) this gives symmetric
         top/bottom breathing so the header, card and language switcher read as evenly spaced; and
         under overflow (short phones) the page grows to its content so centring is a no-op and the
         column simply scrolls from the top. The tightened spacing below is what makes the switcher
         fit within the viewport at 844h; centring keeps the leftover space balanced. */
      justify-content: center !important;
    }
    .language-switcher {
      /* Hug the card so the picker fits without scrolling in password mode on normal-height
         phones; on shorter phones the column simply scrolls. */
      margin: 8px auto 0 !important;
    }
    .footer {
      display: flex !important;
      flex-wrap: wrap !important;
      justify-content: flex-start !important;
      align-items: center !important;
      gap: 8px !important;
      text-align: left !important;
    }
    .footer .label,
    .footer .link {
      white-space: nowrap !important;
    }
  }
`;
